/**
 * Industry Selection Component
 * Two-tier dropdown system with search functionality for industry selection
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Search } from 'lucide-react';

const IndustrySelection = ({
  // State
  industries,
  industrySearch,
  searchResults,
  showSearchDropdown,
  selectedParent,
  formData,
  // Handlers
  onIndustrySearchChange,
  onSelectIndustryFromSearch,
  onParentChange,
  onInputChange,
  onDropdownClose
}) => {
  // Extract unique industry parents for first dropdown
  const industryParents = [...new Set(industries.map(ind => ind.industry_parent).filter(Boolean))].sort();

  // Filter industries by selected parent for second dropdown
  const filteredIndustries = selectedParent
    ? industries.filter(ind => ind.industry_parent === selectedParent)
    : [];

  // Get selected industry details
  const selectedIndustry = industries.find(ind => ind.id === parseInt(formData.industry_id));

  return (
    <div>
      {/* Business Information Header with Industry Search Box */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">Business Information</h2>

        {/* Industry Search Box */}
        <div className="relative w-64 industry-search-container">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search industries..."
              value={industrySearch}
              onChange={(e) => onIndustrySearchChange(e.target.value)}
              onFocus={() => industrySearch.length >= 2 && showSearchDropdown}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Dropdown */}
          {showSearchDropdown && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {searchResults.map((industry) => (
                <button
                  key={industry.id}
                  type="button"
                  onClick={() => onSelectIndustryFromSearch(industry)}
                  className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="text-sm font-medium text-gray-900">{industry.industry_name} ({industry.business_type === 'private' ? 'Private' : 'Public'})</div>
                  <div className="text-xs text-gray-500">{industry.naics_code} • {industry.industry_parent}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Industry Selection - Two-tier dropdown */}
      <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          Industry Category *
        </label>
        <select
          value={selectedParent}
          onChange={onParentChange}
          required
          className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
        >
          <option value="">Select industry category...</option>
          <option value="MANUAL">Manual / Other Industry</option>
          {industryParents.map(parent => (
            <option key={parent} value={parent}>
              {parent}
            </option>
          ))}
        </select>

        {selectedParent === 'MANUAL' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
              Enter Industry Name *
            </label>
            <input
              type="text"
              name="manual_industry_name"
              value={formData.manual_industry_name}
              onChange={onInputChange}
              placeholder="e.g., Custom Manufacturing, Specialty Retail..."
              required
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              When using manual industry entry, only DCF, Capitalization of Earnings, NAV, and Manual Multiple valuation methods are available.
            </p>
          </>
        )}

        {selectedParent && selectedParent !== 'MANUAL' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
              Specific Industry *
            </label>
            <select
              name="industry_id"
              value={formData.industry_id}
              onChange={onInputChange}
              required
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select specific industry...</option>
              {filteredIndustries.map(industry => (
                <option key={industry.id} value={industry.id}>
                  {industry.industry_name} ({industry.business_type === 'private' ? 'Private' : 'Public'})
                </option>
              ))}
            </select>
          </>
        )}
        {selectedIndustry && selectedIndustry.multiples && (
          <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
            <div className="flex items-center justify-between">
              <p className="font-medium text-blue-900">Industry Multiples ({selectedIndustry.business_type === 'private' ? 'Private' : 'Public'} Companies)</p>
              <div className="flex items-center gap-3 text-blue-700">
                <div>EV/EBITDA: <span className="font-semibold">{selectedIndustry.multiples.ev_ebitda?.median || 'N/A'}x</span></div>
                <div>EV/Revenue: <span className="font-semibold">{selectedIndustry.multiples.ev_sales?.median || 'N/A'}x</span></div>
                <div>EBITDA Margin: <span className="font-semibold">{selectedIndustry.margins?.ebitda_margin?.median || 'N/A'}%</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

IndustrySelection.propTypes = {
  industries: PropTypes.array.isRequired,
  industrySearch: PropTypes.string.isRequired,
  searchResults: PropTypes.array.isRequired,
  showSearchDropdown: PropTypes.bool.isRequired,
  selectedParent: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  onIndustrySearchChange: PropTypes.func.isRequired,
  onSelectIndustryFromSearch: PropTypes.func.isRequired,
  onParentChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onDropdownClose: PropTypes.func
};

export default IndustrySelection;
